import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { userService } from "../../services/userApi";

// Where a banner's link_to/link_id points on the website (same targets as the app).
const bannerTarget = ({ link_to: linkTo, link_id: linkId }) => {
  switch (linkTo) {
    case "url":
      return linkId ? { href: /^https?:\/\//i.test(linkId) ? linkId : `https://${linkId}` } : null;
    case "lawyer":
    case "company":
      return linkId ? { to: `/user/lawyers/${linkId}` } : null;
    case "category":
      return linkId ? { to: `/user/lawyers?category=${linkId}` } : null;
    case "new_call":
      return { to: "/user/video-call" };
    // The website has no separate case form; questions are where users post legal issues.
    case "new_case":
    case "new_question":
      return { to: "/user/requests/new" };
    default:
      return null;
  }
};

// Marketing banner from getDashboard, shown once as a popup over the user's home.
const MarketingBanner = ({ banner, onClose }) => {
  const navigate = useNavigate();
  const bannerId = banner?.id;

  // Record the view as soon as it's shown, so the API applies once/repeat rules.
  useEffect(() => {
    if (!bannerId) return undefined;
    userService.markBannerViewed(bannerId).catch(() => {});
    const onKey = (e) => e.key === "Escape" && onClose();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [bannerId, onClose]);

  if (!bannerId) return null;

  const target = bannerTarget(banner);
  const open = () => {
    onClose();
    if (target?.href) window.open(target.href, "_blank", "noopener,noreferrer");
    else if (target?.to) navigate(target.to);
  };

  const image = <img src={banner.image} alt="Offer from Legal Platform" className="usp-banner-img" />;

  return createPortal(
    <div className="lwp usp-banner-root" role="presentation" onMouseDown={onClose}>
      <div className="usp-banner" role="dialog" aria-modal="true" aria-label="Announcement" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="usp-banner-close" onClick={onClose} aria-label="Close">
          <i className="bi bi-x-lg" />
        </button>
        {target ? (
          <button type="button" className="usp-banner-link" onClick={open}>
            {image}
          </button>
        ) : (
          image
        )}
      </div>
    </div>,
    document.body
  );
};

export default MarketingBanner;
